import React, { useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { deleteDealFolder } from '../../../../api/mutations';
import { getDealsForTeam } from '../../../../api/queries';
import { getDealFolders } from '../../../../api/queries_v2';
import { useNotifications } from '../../../../notifications';
import { TitleFormattedMessage } from '../../../../components/titleFormattedMessage';
import PropdocsModal from '../../../../components/modal/PropdocsModal';

interface DeleteDealFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  teamId: string;
  onSuccess: () => void;
}

const DeleteDealFolderModal: React.FC<DeleteDealFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  teamId,
  onSuccess,
}) => {
  const [confirmationStep, setConfirmationStep] = useState<'initial' | 'final'>('initial');
  const [isDeleting, setIsDeleting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [deleteFolder] = useMutation(deleteDealFolder, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder deleted successfully. All deals have been moved to the root folder.' });
      onSuccess();
      handleClose();
      setIsDeleting(false);
    },
    onError: (error) => {
      console.error('Error deleting folder:', error);
      notifyError({ message: error.message || 'Failed to delete folder. Please try again.' });
      setIsDeleting(false);
    },
  });

  const handleClose = () => {
    setConfirmationStep('initial');
    setIsDeleting(false);
    onClose();
  };

  const handleContinue = () => {
    setConfirmationStep('final');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteFolder({
        variables: {
          id: folderId,
          force: true, // Force delete with contents
        },
        refetchQueries: [
          {
            query: getDealFolders,
            variables: { teamId },
          },
          {
            query: getDealsForTeam,
            variables: { teamId },
          },
        ],
      });
    } catch (error) {
      // Error handling is done in the onError callback
      console.error('Delete folder error:', error);
    }
  };

  const renderInitialConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="deals.folders.deleting"
              defaultMessage="Deleting: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="warning">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="warning.title" defaultMessage="Warning" />
            </strong>
          </p>
          <p>
            <FormattedMessage
              id="deals.folders.delete.warning.with-contents"
              defaultMessage="This folder may contain deals and/or subfolders. Deleting this folder will:"
            />
          </p>
          <ul>
            <li>
              <FormattedMessage
                id="deals.folders.delete.warning.move-deals"
                defaultMessage="Move all deals to the root level (they will NOT be deleted)"
              />
            </li>
            <li>
              <FormattedMessage
                id="deals.folders.delete.warning.delete-subfolders"
                defaultMessage="Permanently delete all subfolders"
              />
            </li>
            <li>
              <FormattedMessage
                id="deals.folders.delete.warning.move-nested-deals"
                defaultMessage="Move all deals from nested folders to the root level"
              />
            </li>
          </ul>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="deals.folders.delete.warning.irreversible"
                defaultMessage="This action cannot be undone."
              />
            </strong>
          </p>
        </Alert>

        <p>
          <FormattedMessage
            id="deals.folders.delete.continue-question"
            defaultMessage="Are you sure you want to continue?"
          />
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="warning" onClick={handleContinue}>
          <TitleFormattedMessage id="cta.continue" defaultMessage="Continue" />
        </Button>
      </div>
    </>
  );

  const renderFinalConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="deals.folders.confirm-deleting"
              defaultMessage="Confirm deletion of: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="danger">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="danger.final-warning" defaultMessage="Final Warning" />
            </strong>
          </p>
          <p>
            <FormattedMessage
              id="deals.folders.delete.final-warning"
              defaultMessage="You are about to permanently delete this folder. All deals inside will be moved to the root level and remain accessible. Any subfolders will be permanently deleted."
            />
          </p>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="deals.folders.delete.are-you-sure"
                defaultMessage="Are you absolutely sure you want to delete this folder?"
              />
            </strong>
          </p>
        </Alert>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isDeleting}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? (
            <FormattedMessage id="cta.deleting" defaultMessage="Deleting..." />
          ) : (
            <TitleFormattedMessage id="cta.delete-folder" defaultMessage="Delete Folder" />
          )}
        </Button>
      </div>
    </>
  );

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isDeleting}
      title={
        confirmationStep === 'initial' ? (
          <FormattedMessage id="deals.folders.delete.title" defaultMessage="Delete Folder" />
        ) : (
          <FormattedMessage id="deals.folders.delete.confirm-title" defaultMessage="Confirm Folder Deletion" />
        )
      }
    >
      {confirmationStep === 'initial' ? renderInitialConfirmation() : renderFinalConfirmation()}
    </PropdocsModal>
  );
};

export default DeleteDealFolderModal;
